import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';
import LoadingRing from '../ui-components/LoadingRing';
import { cn } from '../../lib/utils';
import { useToast } from '../../hooks/use-toast';
import {
    addPoliciesToFolder,
    removePoliciesFromFolder,
    PolicyMember,
} from '../../services/api/folderService';
import {
    getCompletedPolicies,
    PolicyListItem,
} from '../../services/api/policyService';

interface ManagePoliciesDialogProps {
    folderId: string;
    existingPolicies: PolicyMember[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onChanged: () => void;
}

const policyTitle = (
    p: { document_id?: { title?: string }; title?: string },
    fallback: string
): string => p.document_id?.title ?? p.title ?? fallback;

interface PolicyRowProps {
    label: string;
    variant: 'existing' | 'pending';
    actionLabel: string;
    onAction: () => void;
    loading?: boolean;
}

const PolicyRow = ({
    label,
    variant,
    actionLabel,
    onAction,
    loading,
}: PolicyRowProps) => (
    <li
        className={cn(
            'flex items-center justify-between gap-3 rounded-lg border border-subtle bg-card px-4 py-3',
            variant === 'pending' && 'border-dashed'
        )}
    >
        <div className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
            {label}
        </div>
        <Button
            variant="outline"
            size="sm"
            className={
                variant === 'existing' ? 'hover:text-destructive' : undefined
            }
            disabled={loading}
            onClick={onAction}
        >
            {loading ? (
                <LoadingRing size="small" />
            ) : (
                <>
                    <X className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    {actionLabel}
                </>
            )}
        </Button>
    </li>
);

export const ManagePoliciesDialog = ({
    folderId,
    existingPolicies,
    open,
    onOpenChange,
    onChanged,
}: ManagePoliciesDialogProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [allPolicies, setAllPolicies] = useState<PolicyListItem[]>([]);
    const [pendingPolicies, setPendingPolicies] = useState<
        Map<string, PolicyListItem>
    >(new Map());
    const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [addPopoverOpen, setAddPopoverOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setPendingPolicies(new Map());
            setPendingRemoveId(null);
            setAddPopoverOpen(false);
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                const result = await getCompletedPolicies({ limit: 500 });
                setAllPolicies(result.data ?? []);
            } catch {
                toast({
                    title: t('folders.common.error'),
                    description: t(
                        'folders.managePolicies.toasts.loadFailed'
                    ),
                    variant: 'destructive',
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open, t, toast]);

    const linkedIds = useMemo(
        () => new Set(existingPolicies.map((p) => p._id)),
        [existingPolicies]
    );

    const availablePolicies = allPolicies.filter(
        (p) => !linkedIds.has(p._id) && !pendingPolicies.has(p._id)
    );

    const togglePending = (policy: PolicyListItem) => {
        setPendingPolicies((prev) => {
            const next = new Map(prev);
            if (next.has(policy._id)) next.delete(policy._id);
            else next.set(policy._id, policy);
            return next;
        });
    };

    const undoPending = (policyId: string) => {
        setPendingPolicies((prev) => {
            const next = new Map(prev);
            next.delete(policyId);
            return next;
        });
    };

    const handleRemove = async (policyId: string) => {
        setPendingRemoveId(policyId);
        try {
            await removePoliciesFromFolder(folderId, [policyId]);
            onChanged();
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.managePolicies.toasts.removeFailed'),
                variant: 'destructive',
            });
        } finally {
            setPendingRemoveId(null);
        }
    };

    const handleSubmit = async () => {
        if (pendingPolicies.size === 0) return;
        setSubmitting(true);
        try {
            await addPoliciesToFolder(
                folderId,
                Array.from(pendingPolicies.keys())
            );
            const count = pendingPolicies.size;
            toast({
                title: t('folders.managePolicies.addedToast', { count }),
            });
            onChanged();
            onOpenChange(false);
        } catch {
            toast({
                title: t('folders.common.error'),
                description: t('folders.managePolicies.toasts.addFailed'),
                variant: 'destructive',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const selectedCount = pendingPolicies.size;
    const pendingList = Array.from(pendingPolicies.values());
    const isEmpty =
        existingPolicies.length === 0 && pendingList.length === 0;
    const unnamed = t('folders.managePolicies.unnamed');

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {t('folders.managePolicies.title')}
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <LoadingRing size="small" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {isEmpty ? (
                            <p className="py-4 text-sm text-subtle-foreground">
                                {t('folders.managePolicies.emptyLinked')}
                            </p>
                        ) : (
                            <ul className="max-h-72 space-y-2 overflow-y-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                                {existingPolicies.map((policy) => (
                                    <PolicyRow
                                        key={policy._id}
                                        label={policyTitle(policy, unnamed)}
                                        variant="existing"
                                        actionLabel={t(
                                            'folders.managePolicies.remove'
                                        )}
                                        onAction={() =>
                                            handleRemove(policy._id)
                                        }
                                        loading={pendingRemoveId === policy._id}
                                    />
                                ))}
                                {pendingList.map((policy) => (
                                    <PolicyRow
                                        key={policy._id}
                                        label={policyTitle(policy, unnamed)}
                                        variant="pending"
                                        actionLabel={t(
                                            'folders.managePolicies.undo'
                                        )}
                                        onAction={() =>
                                            undoPending(policy._id)
                                        }
                                    />
                                ))}
                            </ul>
                        )}

                        {availablePolicies.length > 0 && (
                            <Popover
                                open={addPopoverOpen}
                                onOpenChange={setAddPopoverOpen}
                            >
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-2 w-full"
                                    >
                                        <Plus
                                            className="mr-2 h-3.5 w-3.5"
                                            aria-hidden="true"
                                        />
                                        {t(
                                            'folders.managePolicies.addPolicy'
                                        )}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent
                                    align="start"
                                    className="w-[var(--radix-popover-trigger-width)] p-0"
                                >
                                    <Command>
                                        <CommandInput
                                            placeholder={t(
                                                'folders.managePolicies.search'
                                            )}
                                        />
                                        <CommandList
                                            onWheel={(e) =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <CommandEmpty>
                                                {t(
                                                    'folders.managePolicies.noMatching'
                                                )}
                                            </CommandEmpty>
                                            <CommandGroup>
                                                {availablePolicies.map(
                                                    (policy) => (
                                                        <CommandItem
                                                            key={policy._id}
                                                            value={`${policyTitle(policy, '')} ${policy._id}`}
                                                            onSelect={() => {
                                                                togglePending(
                                                                    policy
                                                                );
                                                                setAddPopoverOpen(
                                                                    false
                                                                );
                                                            }}
                                                        >
                                                            <span className="truncate text-sm font-medium">
                                                                {policyTitle(
                                                                    policy,
                                                                    unnamed
                                                                )}
                                                            </span>
                                                        </CommandItem>
                                                    )
                                                )}
                                            </CommandGroup>
                                        </CommandList>
                                    </Command>
                                </PopoverContent>
                            </Popover>
                        )}
                    </div>
                )}

                <DialogFooter className="items-center justify-between sm:justify-between">
                    <span className="text-xs text-subtle-foreground">
                        {selectedCount > 0 &&
                            t('folders.managePolicies.selected', {
                                count: selectedCount,
                            })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant={selectedCount > 0 ? 'outline' : 'default'}
                            onClick={() => onOpenChange(false)}
                            disabled={submitting}
                        >
                            {selectedCount > 0
                                ? t('folders.managePolicies.cancel')
                                : t('folders.managePolicies.done')}
                        </Button>
                        {selectedCount > 0 && (
                            <Button
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <LoadingRing size="small" />
                                ) : (
                                    t('folders.managePolicies.add', {
                                        count: selectedCount,
                                    })
                                )}
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
